import {Async} from 'Core/models/Async';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {CaseNumber, UserAssignedCaseNumber, CaseNumberError, matchCaseNumber} from './CaseNumber';
import {NonEmptyString, StringUnion, EmptyString} from 'Core/models/EmptyString';
import {AgentList, EmptyAgentList, ValidAgentList} from './AgentList';

export class UnvalidatedNewYorkVerifyCriticalInformation {
  public readonly key = 'UnvalidatedNewYorkVerifyCriticalInformation';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly accountNumber: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList
  ) {}
}

export class InvalidNewYorkVerifyCriticalInformation {
  public readonly key = 'InvalidNewYorkVerifyCriticalInformation';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly accountNumber: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList
  ) {}
}

type NewYorkVerifyCriticalInformationProps = {
  caseNumber: CaseNumber;
  caseName: StringUnion;
  applicant: StringUnion;
  controlNumber: StringUnion;
  transit: StringUnion;
  accountNumber: StringUnion;
  agentList: AgentList;
};

export class ValidNewYorkVerifyCriticalInformation {
  public static createFromApp({
    caseNumber,
    caseName,
    transit,
    accountNumber,
    controlNumber,
    applicant,
    agentList,
  }: NewYorkVerifyCriticalInformationProps) {
    return this.create(caseNumber, caseName, transit, accountNumber, controlNumber, applicant, agentList);
  }

  public static create(
    caseNumber: CaseNumber,
    caseName: StringUnion,
    transit: StringUnion,
    accountNumber: StringUnion,
    controlNumber: StringUnion,
    applicant: StringUnion,
    agentList: AgentList
  ) {
    return new ValidNewYorkVerifyCriticalInformation(
      caseNumber,
      caseName,
      transit,
      accountNumber,
      controlNumber,
      applicant,
      agentList
    );
  }

  public readonly key = 'ValidNewYorkVerifyCriticalInformation';
  private constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly accountNumber: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList
  ) {}
}

export type NewYorkVerifyCriticalInformation =
  | UnvalidatedNewYorkVerifyCriticalInformation
  | InvalidNewYorkVerifyCriticalInformation
  | ValidNewYorkVerifyCriticalInformation;

export type NewYorkVerifyCriticalInformationOptions<TReturn> = ToMatchOptions<
  NewYorkVerifyCriticalInformation,
  'key',
  TReturn
>;

export const matchNewYorkVerifyCriticalInformation = <TReturn>(
  options: NewYorkVerifyCriticalInformationOptions<TReturn>
) => (app: NewYorkVerifyCriticalInformation): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkVerifyCriticalInformation':
      return options.InvalidNewYorkVerifyCriticalInformation(app);
    case 'UnvalidatedNewYorkVerifyCriticalInformation':
      return options.UnvalidatedNewYorkVerifyCriticalInformation(app);
    case 'ValidNewYorkVerifyCriticalInformation':
      return options.ValidNewYorkVerifyCriticalInformation(app);
  }
};

export const changeCaseNumber = (caseNumber: Async<string>) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber,
  });

export const changeUserCaseNumber = (caseNumber: string) => (app: NewYorkVerifyCriticalInformation) => {
  return ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber: UserAssignedCaseNumber.create(caseNumber),
  });
};

export const changeCaseNumberError = (caseNumber: string) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber: CaseNumberError.create(caseNumber),
  });

export const changeCaseName = (caseName: string) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    caseName: NonEmptyString.create(caseName),
  });

export const changeTransit = (transit: string) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    transit: NonEmptyString.create(transit),
  });

export const changeAccountNumber = (accountNumber: string) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    accountNumber: NonEmptyString.create(accountNumber),
  });

export const changeApplicant = (applicant: string) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    applicant: NonEmptyString.create(applicant),
  });

export const changeControlNumber = (controlNumber: string) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    controlNumber: NonEmptyString.create(controlNumber),
  });

export const changeAgentList = (agentList: AgentList) => (app: NewYorkVerifyCriticalInformation) =>
  ValidNewYorkVerifyCriticalInformation.createFromApp({
    ...app,
    agentList: ValidAgentList.create(agentList.agentList),
  });

export const EmptyNewYorkVerifyCriticalInformation = new UnvalidatedNewYorkVerifyCriticalInformation(
  new UserAssignedCaseNumber(''),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyAgentList
);

export const isVerified = (
  originalNewYorkVerifyCriticalInformation: NewYorkVerifyCriticalInformation,
  newYorkVerifyCriticalInformation: NewYorkVerifyCriticalInformation
): boolean => {
  const getCaseNumber = (caseNumber: CaseNumber) =>
    matchCaseNumber({
      Loading: () => '',
      Loaded: () => '',
      Unassigned: () => '',
      UserAssignedCaseNumber: caseNumber => caseNumber,
      Error: () => '',
      CaseNumberError: () => '',
    })(caseNumber)!;

  const {
    agentList,
    caseNumber,
    controlNumber,
    accountNumber,
    transit,
    applicant,
  } = originalNewYorkVerifyCriticalInformation;
  const {
    agentList: newAgentList,
    applicant: newApplicant,
    caseNumber: newCaseNumber,
    controlNumber: newControlNumber,
    accountNumber: newAccountNumber,
    transit: newTransit,
  } = newYorkVerifyCriticalInformation;

  return (
    agentList.agentList.size > 0 &&
    agentList.agentList.count(agentInformation => newAgentList.agentList.toArray().includes(agentInformation)) ===
      agentList.agentList.size &&
    ((getCaseNumber(caseNumber) !== '' &&
      controlNumber.value !== '' &&
      getCaseNumber(caseNumber).toLowerCase() === getCaseNumber(newCaseNumber).toLowerCase() &&
      controlNumber.value === newControlNumber.value) ||
      (accountNumber.value !== '' &&
        transit.value !== '' &&
        accountNumber.value === newAccountNumber.value &&
        transit.value === newTransit.value)) &&
    applicant.value !== '' &&
    applicant.value.toLowerCase() === newApplicant.value.toLowerCase()
  );
};

export const isValidNewYorkVerifyCriticalInformation = (
  newYorkVerifyCriticalInformation: NewYorkVerifyCriticalInformation
): newYorkVerifyCriticalInformation is ValidNewYorkVerifyCriticalInformation => {
  return newYorkVerifyCriticalInformation.key === 'ValidNewYorkVerifyCriticalInformation';
};
